import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getFeaturedPaintings } from '../../data/paintings';
import useScrollReveal from '../../hooks/useScrollReveal';

const FeaturedSlider = ({ onViewDetails }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [ref, isVisible] = useScrollReveal();
  const featuredPaintings = getFeaturedPaintings();
  const { t, i18n } = useTranslation();

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) =>
      prev === featuredPaintings.length - 1 ? 0 : prev + 1
    );
  }, [featuredPaintings.length]);

  const prevSlide = () => {
    setCurrentSlide((prev) =>
      prev === 0 ? featuredPaintings.length - 1 : prev - 1
    );
  };

  // Auto-play
  useEffect(() => {
    if (!isAutoPlaying || !isVisible) return;

    const timer = setInterval(nextSlide, 6000);
    return () => clearInterval(timer);
  }, [isAutoPlaying, isVisible, nextSlide]);

  const currentPainting = featuredPaintings[currentSlide];
  const isEnglish = i18n.language?.startsWith('en');

  return (
    <section
      ref={ref}
      className="py-12 xs:py-16 sm:py-24 bg-white"
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}
    >
      <div className="max-w-7xl mx-auto px-4 xs:px-6 lg:px-8">
        {/* Header */}
        <div className={`text-center mb-8 xs:mb-12 sm:mb-16 reveal ${isVisible ? 'visible' : ''}`}>
          <span className="text-xs xs:text-sm text-[#B8963E] tracking-widest uppercase mb-3 xs:mb-4 block">
            {t('featured.label')}
          </span>
          <h2 className="text-responsive-2xl mb-4 xs:mb-6 text-[#3D3831]">
            {t('featured.title')}
          </h2>
          <div className="divider-elegant" />
        </div>

        {/* Slider */}
        <div className={`reveal reveal-delay-2 ${isVisible ? 'visible' : ''}`}>
          <div className="relative">
            <div className="flex flex-col lg:flex-row items-stretch bg-[#FAF7F2] overflow-hidden">
              {/* Image Side */}
              <div className="lg:w-2/3 relative h-[280px] xs:h-[350px] sm:h-[400px] lg:h-[600px] overflow-hidden">
                {featuredPaintings.map((painting, index) => (
                  <div
                    key={painting.id}
                    className={`absolute inset-0 transition-all duration-1000 ${
                      index === currentSlide
                        ? 'opacity-100 scale-100'
                        : 'opacity-0 scale-105'
                    }`}
                  >
                    <img
                      src={painting.url}
                      alt={isEnglish ? painting.titleEn || painting.title : painting.title}
                      sizes="(max-width: 1023px) 100vw, 66vw"
                      loading={index === 0 ? 'eager' : 'lazy'}
                      decoding="async"
                      className="w-full h-full object-cover"
                    />
                    {/* Subtle gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#FAF7F2]/20 lg:hidden" />
                  </div>
                ))}

                {/* Navigation Arrows - touch-friendly 44x44px minimum */}
                <div className="absolute bottom-4 xs:bottom-6 left-4 xs:left-6 flex space-x-2">
                  <button
                    onClick={prevSlide}
                    className="w-11 h-11 xs:w-12 xs:h-12 bg-white/90 backdrop-blur-sm flex items-center justify-center text-[#3D3831] hover:bg-white transition-all duration-300"
                    aria-label={t('featured.previous')}
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    onClick={nextSlide}
                    className="w-11 h-11 xs:w-12 xs:h-12 bg-white/90 backdrop-blur-sm flex items-center justify-center text-[#3D3831] hover:bg-white transition-all duration-300"
                    aria-label={t('featured.next')}
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>

              {/* Info Side */}
              <div className="lg:w-1/3 p-4 xs:p-6 sm:p-8 lg:p-12 flex flex-col justify-center">
                {/* Slide Counter */}
                <div className="flex items-center space-x-3 xs:space-x-4 mb-4 xs:mb-6 sm:mb-8">
                  <span className="text-2xl xs:text-3xl sm:text-4xl font-light text-[#B8963E]">
                    {String(currentSlide + 1).padStart(2, '0')}
                  </span>
                  <div className="flex-1 h-px bg-[#E8E2D9]" />
                  <span className="text-xs xs:text-sm text-[#8B7E6A]">
                    {String(featuredPaintings.length).padStart(2, '0')}
                  </span>
                </div>

                {/* Content */}
                <div className="overflow-hidden">
                  <h3
                    key={currentPainting.id + '-title'}
                    className="text-xl xs:text-2xl lg:text-3xl mb-2 xs:mb-3 text-[#3D3831] animate-[slideInRight_0.5s_ease]"
                  >
                    {isEnglish ? currentPainting.titleEn || currentPainting.title : currentPainting.title}
                  </h3>
                </div>

                <p className="text-sm xs:text-base text-[#8B7E6A] mb-3 xs:mb-4">
                  {currentPainting.year} · {currentPainting.technique}
                </p>

                <p className="text-sm xs:text-base text-[#5A5249] leading-relaxed mb-4 xs:mb-6 sm:mb-8 line-clamp-3 xs:line-clamp-4">
                  {currentPainting.description}
                </p>

                {currentPainting.price && currentPainting.status === 'available' && (
                  <p className="text-lg xs:text-xl text-[#B8963E] font-medium mb-4 xs:mb-6 sm:mb-8">
                    {currentPainting.price} ₽
                  </p>
                )}

                <button
                  onClick={() => onViewDetails(currentPainting)}
                  className="btn-outline self-start xs:self-start w-full xs:w-auto"
                >
                  {t('featured.details')}
                </button>
              </div>
            </div>

            {/* Dots - larger touch targets */}
            <div className="flex justify-center space-x-3 mt-6 xs:mt-8">
              {featuredPaintings.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`h-2 xs:h-1 rounded-full transition-all duration-500 ${
                    index === currentSlide
                      ? 'w-8 bg-[#B8963E]'
                      : 'w-3 xs:w-2 bg-[#E8E2D9] hover:bg-[#C4B9A8]'
                  }`}
                  aria-label={`${t('featured.slide')} ${index + 1}`}
                  style={{ minHeight: '24px', minWidth: '24px' }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedSlider;
